import * as RadioGroup from '@givebutter/primitives/radio-group';
import { PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import * as Form from 'components/form';
import { PledgeCoveredFee, PledgeCoveredFeePolicy } from 'constants/fees';
import { PledgeStatus } from 'constants/pledges';

const PledgeSettingsForm = ({ onChange }: { onChange?: () => void }) => {
    const { control, path, watch } = Form.useGivebutterFormContext<PledgeFormValue>();

    const feeCoverageVisibility = watch('fee_coverage_visibility');
    const status = watch('status_friendly');

    return (
        <div className="flex flex-col gap-6 border-t border-t-gray-200 pt-6">
            <Form.Container className={'flex flex-row gap-4'}>
                <Form.Label name={path('auto_remind')}>
                    Send automatic reminders
                    <div className="font-normal text-gray-600">
                        We'll send the pledgee a reminder 48 hours before the pledge fulfillment
                        date.
                    </div>
                </Form.Label>
                <Form.Switch
                    control={control}
                    name={'auto_remind'}
                    onCheckedChange={onChange}
                    disabled={status === PledgeStatus.Canceled}
                />
            </Form.Container>
            <Form.Container className={'flex flex-row justify-between gap-4'}>
                <Form.Label name={path('allow_custom_amount')}>
                    Allow supporters to pay a custom amount
                    <div className="font-normal text-gray-600">
                        The supporter will be able to enter a custom amount different from the set
                        installment amount.
                    </div>
                </Form.Label>
                <Form.Switch
                    control={control}
                    name={'allow_custom_amount'}
                    onCheckedChange={onChange}
                    disabled={status === PledgeStatus.Canceled}
                />
            </Form.Container>
            <Form.Container className={'flex flex-row justify-between gap-4'}>
                <Form.Label name={path('include_in_campaign_goal')}>
                    Include payments in campaign total
                    <div className="font-normal text-gray-600">
                        Payments for this pledge will count toward the campaign's total and goal
                        bar.
                    </div>
                </Form.Label>
                <Form.Switch
                    control={control}
                    name={'include_in_campaign_goal'}
                    onCheckedChange={onChange}
                    disabled={status === PledgeStatus.Canceled}
                />
            </Form.Container>
            <Form.Container className="gap-2">
                <p className="text-sm font-medium text-gray-900">Fee settings</p>
                <Form.UseFormRadioGroup
                    className="flex flex-col gap-3"
                    name="fee_coverage_visibility"
                    control={control}
                    onChange={onChange}
                    disabled={status === PledgeStatus.Canceled}
                >
                    <RadioGroup.ItemContainer>
                        <RadioGroup.Item
                            value={PledgeCoveredFeePolicy.AskToCoverFees}
                            id="fee_coverage_visibility_optional"
                        >
                            <RadioGroup.Indicator />
                        </RadioGroup.Item>
                        <div className="flex flex-col gap-1">
                            <Form.Label
                                name={path('fee_coverage_visibility')}
                                className="cursor-pointer font-medium leading-none"
                                htmlFor="fee_coverage_visibility_optional"
                            >
                                Ask to cover fees
                            </Form.Label>
                            <p className="text-sm text-gray-600">
                                Ask supporters to cover fees when making a pledge payment.
                            </p>
                        </div>
                    </RadioGroup.ItemContainer>
                    <RadioGroup.ItemContainer>
                        <RadioGroup.Item
                            value={PledgeCoveredFeePolicy.RequireFees}
                            id="fee_coverage_visibility_required"
                        >
                            <RadioGroup.Indicator />
                        </RadioGroup.Item>
                        <div className="flex flex-col gap-1">
                            <Form.Label
                                name={path('fee_coverage_visibility')}
                                className="cursor-pointer font-medium leading-none"
                                htmlFor="fee_coverage_visibility_required"
                            >
                                Require fees
                            </Form.Label>
                            <p className="text-sm text-gray-600">
                                Supporters are required to cover fees with each pledge payment.
                            </p>
                        </div>
                    </RadioGroup.ItemContainer>
                    <RadioGroup.ItemContainer>
                        <RadioGroup.Item
                            value={PledgeCoveredFeePolicy.HideFees}
                            id="fee_coverage_visibility_hidden"
                        >
                            <RadioGroup.Indicator />
                        </RadioGroup.Item>
                        <div className="flex flex-col gap-1">
                            <Form.Label
                                name={path('fee_coverage_visibility')}
                                className="cursor-pointer font-medium leading-none"
                                htmlFor="fee_coverage_visibility_hidden"
                            >
                                Hide fees
                            </Form.Label>
                            <p className="text-sm text-gray-600">
                                Hide all transaction fees from supporters while making pledge
                                payments.
                            </p>
                        </div>
                    </RadioGroup.ItemContainer>
                </Form.UseFormRadioGroup>
            </Form.Container>
            {feeCoverageVisibility !== PledgeCoveredFeePolicy.HideFees && (
                <Form.Container className="gap-2">
                    <p className="text-sm font-medium text-gray-900">Fees shown</p>
                    <Form.UseFormRadioGroup
                        className="flex w-fit flex-col gap-3"
                        name="fee_coverage_type"
                        control={control}
                        onChange={onChange}
                        disabled={status === PledgeStatus.Canceled}
                    >
                        <RadioGroup.ItemContainer>
                            <RadioGroup.Item
                                value={PledgeCoveredFee.All}
                                id="fee_coverage_type_all"
                            >
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <div className="flex flex-col gap-1">
                                <Form.Label
                                    name={path('fee_coverage_type')}
                                    className="cursor-pointer font-medium leading-none"
                                    htmlFor="fee_coverage_type_all"
                                >
                                    All
                                </Form.Label>
                            </div>
                        </RadioGroup.ItemContainer>
                        <RadioGroup.ItemContainer>
                            <RadioGroup.Item
                                value={PledgeCoveredFee.OnlyProcessing}
                                id="fee_coverage_type_processing"
                            >
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <div className="flex flex-col gap-1">
                                <Form.Label
                                    name={path('fee_coverage_type')}
                                    className="cursor-pointer font-medium leading-none"
                                    htmlFor="fee_coverage_type_processing"
                                >
                                    Processing fees only
                                </Form.Label>
                                <p className="text-sm text-gray-600">
                                    Transactions have a processing fee of 2.9% + 30¢ per
                                    transaction.
                                </p>
                            </div>
                        </RadioGroup.ItemContainer>
                        <RadioGroup.ItemContainer>
                            <RadioGroup.Item
                                value={PledgeCoveredFee.OnlyPlatform}
                                id="fee_coverage_type_transaction"
                            >
                                <RadioGroup.Indicator />
                            </RadioGroup.Item>
                            <div className="flex flex-col gap-1">
                                <Form.Label
                                    name={path('fee_coverage_type')}
                                    className="cursor-pointer font-medium leading-none"
                                    htmlFor="fee_coverage_type_transaction"
                                >
                                    Platform fees only
                                </Form.Label>
                                <p className="text-sm text-gray-600">
                                    All pledge transactions have a 1.5% platform fee. These fees
                                    help cover the cost of the services Givebutter provides.
                                </p>
                            </div>
                        </RadioGroup.ItemContainer>
                    </Form.UseFormRadioGroup>
                </Form.Container>
            )}
        </div>
    );
};

export { PledgeSettingsForm };
